import { Button, Menu, MenuItem, Stack, SvgIcon } from '@mui/material'
import { LocalizationProvider, DesktopDatePicker } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { IconChevronDown, IconX } from '@tabler/icons-react'
import { startOfMonth, endOfMonth, subMonths, startOfDay, endOfDay } from 'date-fns'
import { useCallback, useMemo, useState } from 'react'

type DateRangeShortcut = 'this-month' | 'last-month' | 'last-3-months' | 'last-6-months'

const DATE_RANGE_SHORTCUTS: Array<{ value: DateRangeShortcut; label: string }> = [
  { value: 'this-month', label: 'This month' },
  { value: 'last-month', label: 'Last month' },
  { value: 'last-3-months', label: 'Last 3 months' },
  { value: 'last-6-months', label: 'Last 6 months' },
]

const getDateRangeFromShortcut = (shortcut: DateRangeShortcut) => {
  const now = new Date()

  switch (shortcut) {
    case 'this-month':
      return {
        start: startOfDay(startOfMonth(now)),
        end: endOfDay(endOfMonth(now)),
      }
    case 'last-month': {
      const lastMonth = subMonths(now, 1)
      return {
        start: startOfDay(startOfMonth(lastMonth)),
        end: endOfDay(endOfMonth(lastMonth)),
      }
    }
    case 'last-3-months': {
      const threeMonthsAgo = subMonths(now, 3)
      return {
        start: startOfDay(startOfMonth(threeMonthsAgo)),
        end: endOfDay(now),
      }
    }
    case 'last-6-months': {
      const sixMonthsAgo = subMonths(now, 6)
      return {
        start: startOfDay(startOfMonth(sixMonthsAgo)),
        end: endOfDay(now),
      }
    }
  }
}

type DatesState = { start: Date | null; end: Date | null }

export const useDateRangePicker = () => {
  const [selectedShortcut, setSelectedShortcut] = useState<DateRangeShortcut | null>(null)
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [dates, setDates] = useState<DatesState>({
    start: null,
    end: null,
  })

  const setStartDate = useCallback(
    (value: Date | null) =>
      setDates((prev) => ({ ...prev, start: value ? startOfDay(value) : null })),
    []
  )
  const setEndDate = useCallback(
    (value: Date | null) => setDates((prev) => ({ ...prev, end: value ? endOfDay(value) : null })),
    []
  )

  const handleShortcutSelect = useCallback((shortcut: DateRangeShortcut) => {
    setSelectedShortcut(shortcut)
    const dateRange = getDateRangeFromShortcut(shortcut)
    setDates(dateRange)
    setAnchorEl(null)
  }, [])

  const handleClearShortcut = useCallback(() => {
    setSelectedShortcut(null)
    setDates({ start: null, end: null })
  }, [])

  const DateRangePicker = useMemo(() => {
    return (
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Stack direction={{ xs: 'column', md: 'row' }} gap={1}>
          {dates.start || dates.end ? (
            <Button
              onClick={() => {
                handleClearShortcut()
                setDates({ start: null, end: null })
              }}
              sx={{ color: 'text.secondary' }}
              endIcon={
                <SvgIcon>
                  <IconX />
                </SvgIcon>
              }
            >
              Clear
            </Button>
          ) : null}
          <DesktopDatePicker
            label={'Select start date'}
            onChange={setStartDate}
            value={dates.start}
            slotProps={{
              field: { clearable: true, onClear: () => setStartDate(null) },
              textField: { size: 'small', sx: { width: 200 } },
            }}
          />
          <DesktopDatePicker
            label={'Select end date'}
            onChange={setEndDate}
            value={dates.end}
            slotProps={{
              field: { clearable: true, onClear: () => setEndDate(null) },
              textField: { size: 'small', sx: { width: 200 } },
            }}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={(event) => setAnchorEl(event.currentTarget)}
            sx={{ color: 'text.secondary' }}
            endIcon={
              <SvgIcon fontSize="small">
                <IconChevronDown />
              </SvgIcon>
            }
          >
            {selectedShortcut
              ? DATE_RANGE_SHORTCUTS.find((s) => s.value === selectedShortcut)?.label
              : 'Quick select'}
          </Button>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={() => setAnchorEl(null)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
          >
            {DATE_RANGE_SHORTCUTS.map((shortcut) => (
              <MenuItem
                key={shortcut.value}
                onClick={() => handleShortcutSelect(shortcut.value)}
                selected={selectedShortcut === shortcut.value}
              >
                {shortcut.label}
              </MenuItem>
            ))}
          </Menu>
        </Stack>
      </LocalizationProvider>
    )
  }, [
    dates.end,
    dates.start,
    setEndDate,
    setStartDate,
    handleClearShortcut,
    handleShortcutSelect,
    selectedShortcut,
    anchorEl,
  ])

  return { DateRangePicker, dates, setDates }
}
